// src/pages/LiveSelling.js — CF live-selling console (cf_engine_v1)
// จัดการ: ไลฟ์ (sessions) + สินค้า + บัญชีรับเงิน + Live Monitor (ออเดอร์เข้า real-time via polling)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import API from '../api';
import toast from 'react-hot-toast';
import { Radio, Plus, Trash2, ArrowLeft, RefreshCw, CreditCard, Package as Pkg } from 'lucide-react';

const money = (n) => Number(n || 0).toLocaleString('en-US');
const btn = { padding: '9px 16px', borderRadius: 9, border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: 14 };
const primary = { ...btn, background: '#2563eb', color: '#fff' };
const ghost = { ...btn, background: '#f1f5f9', color: '#334155' };
const inp = { padding: 9, border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, width: '100%', boxSizing: 'border-box' };
const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 };

const STATUS = {
  cart: ['ตะกร้า', '#64748b'], confirmed: ['ยืนยัน', '#0891b2'],
  awaiting_payment: ['รอสลิป', '#d97706'], address_pending: ['รอที่อยู่', '#d97706'],
  paid: ['จ่ายแล้ว', '#16a34a'], ready_to_ship: ['พร้อมส่ง', '#2563eb'],
  shipped: ['ส่งแล้ว', '#2563eb'], delivered: ['ถึงแล้ว', '#16a34a'], cancelled: ['ยกเลิก', '#dc2626'],
};
const Badge = ({ s }) => {
  const [t, c] = STATUS[s] || [s, '#64748b'];
  return <span style={{ fontSize: 12, fontWeight: 600, color: c, background: c + '18', padding: '2px 9px', borderRadius: 20 }}>{t}</span>;
};

export default function LiveSelling() {
  const [view, setView] = useState('sessions'); // sessions | session | receivers
  const [sessions, setSessions] = useState([]);
  const [receivers, setReceivers] = useState([]);
  const [cur, setCur] = useState(null); // current session

  const loadSessions = useCallback(async () => {
    try { const { data } = await API.get('/api/live/sessions'); setSessions(data || []); }
    catch { toast.error('โหลดไลฟ์ไม่สำเร็จ'); }
  }, []);
  const loadReceivers = useCallback(async () => {
    try { const { data } = await API.get('/api/live/receivers'); setReceivers(data || []); }
    catch { toast.error('โหลดบัญชีไม่สำเร็จ'); }
  }, []);
  useEffect(() => { loadSessions(); loadReceivers(); }, [loadSessions, loadReceivers]);

  return (
    <div style={{ padding: 20, maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <Radio size={26} color="#2563eb" />
        <h1 style={{ margin: 0, fontSize: 22 }}>ไลฟ์ขาย (CF)</h1>
        <div style={{ flex: 1 }} />
        <button style={view === 'sessions' ? primary : ghost} onClick={() => setView('sessions')}>ไลฟ์</button>
        <button style={view === 'receivers' ? primary : ghost} onClick={() => setView('receivers')}>บัญชีรับเงิน</button>
      </div>

      {view === 'sessions' && <Sessions sessions={sessions} receivers={receivers} reload={loadSessions} open={(s) => { setCur(s); setView('session'); }} />}
      {view === 'session' && cur && <SessionDetail session={cur} back={() => { setView('sessions'); loadSessions(); }} />}
      {view === 'receivers' && <Receivers receivers={receivers} reload={loadReceivers} />}
    </div>
  );
}

/* ─────────── Sessions ─────────── */
function Sessions({ sessions, receivers, reload, open }) {
  const [f, setF] = useState({ fb_page_id: '', fb_live_video_id: '', title: '', receiver_id: '' });
  const [show, setShow] = useState(false);
  const create = async () => {
    if (!f.fb_page_id || !f.fb_live_video_id) return toast.error('กรอก Page ID และ Live Video ID');
    try { await API.post('/api/live/sessions', { ...f, receiver_id: f.receiver_id || null }); toast.success('สร้างไลฟ์แล้ว'); setF({ fb_page_id: '', fb_live_video_id: '', title: '', receiver_id: '' }); setShow(false); reload(); }
    catch (e) { toast.error(e.response?.data?.error || 'สร้างไม่สำเร็จ'); }
  };
  return (
    <div>
      <button style={{ ...primary, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => setShow(!show)}><Plus size={16} /> สร้างไลฟ์ใหม่</button>
      {show && (
        <div style={{ ...card, marginBottom: 16, display: 'grid', gap: 10 }}>
          <input style={inp} placeholder="ชื่อไลฟ์ (เช่น ไลฟ์เย็นวันศุกร์)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          <input style={inp} placeholder="Facebook Page ID" value={f.fb_page_id} onChange={(e) => setF({ ...f, fb_page_id: e.target.value })} />
          <input style={inp} placeholder="Live Video ID" value={f.fb_live_video_id} onChange={(e) => setF({ ...f, fb_live_video_id: e.target.value })} />
          <select style={inp} value={f.receiver_id} onChange={(e) => setF({ ...f, receiver_id: e.target.value })}>
            <option value="">บัญชีรับเงิน: ใช้ค่าเริ่มต้น</option>
            {receivers.map((r) => <option key={r.id} value={r.id}>{r.receiver_name || r.promptpay_id}</option>)}
          </select>
          <button style={primary} onClick={create}>สร้าง</button>
        </div>
      )}
      <div style={{ display: 'grid', gap: 10 }}>
        {sessions.length === 0 && <div style={{ color: '#94a3b8', textAlign: 'center', padding: 30 }}>ยังไม่มีไลฟ์ — สร้างไลฟ์ใหม่เพื่อเริ่ม</div>}
        {sessions.map((s) => (
          <div key={s.id} style={{ ...card, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }} onClick={() => open(s)}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{s.title || '(ไม่มีชื่อ)'}</div>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>Live {s.fb_live_video_id}</div>
            </div>
            <Badge s={s.status} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────── Session detail: products + monitor ─────────── */
function SessionDetail({ session, back }) {
  const [tab, setTab] = useState('monitor'); // monitor | products
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [pf, setPf] = useState({ code: '', name: '', price: '', stock: '' });
  const timer = useRef(null);

  const loadProducts = useCallback(async () => {
    try { const { data } = await API.get(`/api/live/sessions/${session.id}/products`); setProducts(data || []); } catch {}
  }, [session.id]);
  const loadOrders = useCallback(async () => {
    try { const { data } = await API.get(`/api/live/sessions/${session.id}/orders`); setOrders(data || []); } catch {}
  }, [session.id]);

  useEffect(() => { loadProducts(); loadOrders(); }, [loadProducts, loadOrders]);
  // Monitor: poll ทุก 4 วิ ขณะเปิดแท็บ monitor
  useEffect(() => {
    if (tab !== 'monitor') return;
    timer.current = setInterval(loadOrders, 4000);
    return () => clearInterval(timer.current);
  }, [tab, loadOrders]);

  const addProduct = async () => {
    if (!pf.code || pf.price === '') return toast.error('กรอกรหัสและราคา');
    try {
      await API.post(`/api/live/sessions/${session.id}/products`, { code: pf.code, name: pf.name || pf.code, price: Number(pf.price), stock: pf.stock === '' ? null : Number(pf.stock) });
      setPf({ code: '', name: '', price: '', stock: '' }); loadProducts();
    } catch (e) { toast.error(e.response?.data?.error || 'เพิ่มไม่สำเร็จ'); }
  };
  const delProduct = async (pid) => { try { await API.delete(`/api/live/products/${pid}`); loadProducts(); } catch {} };
  const endLive = async () => { try { await API.patch(`/api/live/sessions/${session.id}`, { status: 'ended' }); toast.success('ปิดไลฟ์แล้ว'); back(); } catch {} };

  const totalPaid = orders.filter((o) => o.status === 'paid').reduce((s, o) => s + Number(o.total), 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <button style={ghost} onClick={back}><ArrowLeft size={16} /></button>
        <div style={{ flex: 1 }}><b>{session.title || '(ไม่มีชื่อ)'}</b> <Badge s={session.status} /></div>
        {session.status !== 'ended' && <button style={{ ...ghost, color: '#dc2626' }} onClick={endLive}>ปิดไลฟ์</button>}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <button style={tab === 'monitor' ? primary : ghost} onClick={() => setTab('monitor')}>Monitor ({orders.length})</button>
        <button style={tab === 'products' ? primary : ghost} onClick={() => setTab('products')}>สินค้า ({products.length})</button>
      </div>

      {tab === 'products' && (
        <div>
          <div style={{ ...card, marginBottom: 14, display: 'grid', gridTemplateColumns: '80px 1fr 90px 90px 80px', gap: 8, alignItems: 'center' }}>
            <input style={inp} placeholder="รหัส" value={pf.code} onChange={(e) => setPf({ ...pf, code: e.target.value.toUpperCase() })} />
            <input style={inp} placeholder="ชื่อสินค้า" value={pf.name} onChange={(e) => setPf({ ...pf, name: e.target.value })} />
            <input style={inp} placeholder="ราคา" type="number" value={pf.price} onChange={(e) => setPf({ ...pf, price: e.target.value })} />
            <input style={inp} placeholder="สต็อก" type="number" value={pf.stock} onChange={(e) => setPf({ ...pf, stock: e.target.value })} />
            <button style={primary} onClick={addProduct}>เพิ่ม</button>
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {products.map((p) => (
              <div key={p.id} style={{ ...card, display: 'flex', alignItems: 'center', gap: 12 }}>
                <b style={{ width: 60 }}>{p.code}</b>
                <span style={{ flex: 1 }}>{p.name}</span>
                <span>{money(p.price)}฿</span>
                <span style={{ fontSize: 12, color: '#94a3b8', width: 90, textAlign: 'right' }}>{p.stock == null ? 'ไม่จำกัด' : `เหลือ ${p.stock - p.reserved}/${p.stock}`}</span>
                <button style={{ ...ghost, padding: 6 }} onClick={() => delProduct(p.id)}><Trash2 size={15} color="#dc2626" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'monitor' && (
        <div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            <div style={{ ...card, flex: 1, textAlign: 'center' }}><div style={{ fontSize: 12, color: '#94a3b8' }}>ออเดอร์</div><div style={{ fontSize: 22, fontWeight: 700 }}>{orders.length}</div></div>
            <div style={{ ...card, flex: 1, textAlign: 'center' }}><div style={{ fontSize: 12, color: '#94a3b8' }}>จ่ายแล้ว</div><div style={{ fontSize: 22, fontWeight: 700, color: '#16a34a' }}>{orders.filter((o) => o.status === 'paid').length}</div></div>
            <div style={{ ...card, flex: 1, textAlign: 'center' }}><div style={{ fontSize: 12, color: '#94a3b8' }}>ยอดจ่ายแล้ว</div><div style={{ fontSize: 22, fontWeight: 700, color: '#16a34a' }}>{money(totalPaid)}฿</div></div>
            <button style={ghost} onClick={loadOrders}><RefreshCw size={16} /></button>
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {orders.length === 0 && <div style={{ color: '#94a3b8', textAlign: 'center', padding: 30 }}>ยังไม่มีออเดอร์ — รอลูกค้า CF ในไลฟ์</div>}
            {orders.map((o) => (
              <div key={o.id} style={card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <b>{o.fb_user_name || o.fb_user_id}</b>
                  {o.payment_method === 'cod' ? <CreditCard size={14} color="#d97706" /> : null}
                  <div style={{ flex: 1 }} />
                  <Badge s={o.status} />
                  <b>{money(o.total)}฿</b>
                </div>
                <div style={{ fontSize: 13, color: '#475569' }}>
                  {(o.items || []).map((it) => `${it.code}×${it.qty}`).join(' · ') || '—'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────── Receivers ─────────── */
function Receivers({ receivers, reload }) {
  const [f, setF] = useState({ promptpay_id: '', receiver_name: '', bank_account: '', is_default: false });
  const create = async () => {
    if (!f.promptpay_id) return toast.error('กรอกพร้อมเพย์');
    try { await API.post('/api/live/receivers', f); toast.success('เพิ่มบัญชีแล้ว'); setF({ promptpay_id: '', receiver_name: '', bank_account: '', is_default: false }); reload(); }
    catch (e) { toast.error(e.response?.data?.error || 'เพิ่มไม่สำเร็จ'); }
  };
  const setDefault = async (id) => { try { await API.patch(`/api/live/receivers/${id}`, { is_default: true }); reload(); } catch {} };
  const del = async (id) => { try { await API.delete(`/api/live/receivers/${id}`); reload(); } catch {} };
  return (
    <div>
      <div style={{ ...card, marginBottom: 16, display: 'grid', gap: 10 }}>
        <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><Pkg size={16} /> เพิ่มบัญชีรับเงิน</div>
        <input style={inp} placeholder="พร้อมเพย์ (เบอร์/เลขบัตร ปชช.)" value={f.promptpay_id} onChange={(e) => setF({ ...f, promptpay_id: e.target.value })} />
        <input style={inp} placeholder="ชื่อบัญชี (โชว์ให้ลูกค้า)" value={f.receiver_name} onChange={(e) => setF({ ...f, receiver_name: e.target.value })} />
        <input style={inp} placeholder="เลขบัญชีธนาคาร (ไว้ตรวจสลิป)" value={f.bank_account} onChange={(e) => setF({ ...f, bank_account: e.target.value })} />
        <label style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}><input type="checkbox" checked={f.is_default} onChange={(e) => setF({ ...f, is_default: e.target.checked })} /> ตั้งเป็นบัญชีหลัก</label>
        <button style={primary} onClick={create}>เพิ่มบัญชี</button>
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        {receivers.map((r) => (
          <div key={r.id} style={{ ...card, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{r.receiver_name || '(ไม่มีชื่อ)'} {r.is_default && <span style={{ fontSize: 11, color: '#16a34a' }}>● หลัก</span>}</div>
              <div style={{ fontSize: 13, color: '#64748b', fontFamily: 'monospace' }}>{r.promptpay_id}{r.bank_account ? ' · ' + r.bank_account : ''}</div>
            </div>
            {!r.is_default && <button style={ghost} onClick={() => setDefault(r.id)}>ตั้งเป็นหลัก</button>}
            <button style={{ ...ghost, padding: 6 }} onClick={() => del(r.id)}><Trash2 size={15} color="#dc2626" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
